/**
 * Wizard 2 Analytics - GA4 and Facebook Pixel tracking
 */

class Wizard2Analytics {
  constructor(flowController) {
    this.flow = flowController;
    this.flowName = flowController?.getConfig?.().name || 'wizard-2';
    this.hasStarted = false;
    this.lastTraitKey = '';
    this.backendApiUrl = window.__wizard2BackendApiUrl || null;
    if (window.WizardGa4Traits) window.WizardGa4Traits.registerRefresh(() => this.syncTraits());
    this.STEP_ORDER = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'plan_overview', 'profile'
    ];
  }

  gtag() {
    if (typeof window.gtag === 'function') window.gtag.apply(window, arguments);
    else if (Array.isArray(window.dataLayer)) window.dataLayer.push(arguments);
  }

  quizTraits(step) {
    if (!window.WizardGa4Traits || !this.flow?.state) return {};
    return window.WizardGa4Traits.fromFlow(this.flow, step || this.flow.state.currentStep || '', this.STEP_ORDER);
  }

  pushTraits(props, sendEvent) {
    const safe = props && typeof props === 'object' ? props : {};
    const key = JSON.stringify(safe);
    if (key === '{}' || key === this.lastTraitKey) return;
    this.lastTraitKey = key;
    this.gtag('set', 'user_properties', safe);
    if (sendEvent) this.gtag('event', 'wizard_traits', Object.assign({ flow_name: this.flowName }, safe));
  }

  syncTraits() {
    const step = this.flow?.state?.currentStep || '';
    this.pushTraits(this.quizTraits(step), true);
  }

  trackStepView(step) {
    const path = this.buildStepPath(step);
    const location = window.location.origin + path;
    const traits = this.quizTraits(step);
    this.pushTraits(traits, false);
    if (!this.hasStarted && (step === 'get_started' || step === 'name')) {
      this.hasStarted = true;
      this.gtag('event', 'flow_start', Object.assign({ flow_name: this.flowName, step }, traits));
    }
    this.gtag('event', 'page_view', Object.assign({
      page_title: `${this.flowName} - ${step}`,
      page_location: location,
      page_path: path,
      flow_name: this.flowName,
      step
    }, traits));
    this.gtag('event', 'step_view', Object.assign({ flow_name: this.flowName, step, page_path: path }, traits));
    this.trackFacebookStepView(step);
  }

  buildStepPath(step) {
    const config = this.flow.getConfig();
    const prefix = config?.urlPrefix || '/wizard-2';
    if (step === 'get_started') return `${prefix}/quiz/get-started`;
    const quizSteps = ['name', 'spayed', 'age', 'breed', 'weight', 'composition', 'activity', 'allergies', 'snacks', 'email', 'calculation'];
    if (quizSteps.includes(step)) return `${prefix}/quiz/${step}`;
    if (step === 'plan_overview') return `${prefix}/plan/overview`;
    if (step === 'profile') {
      const metaobjectId = this.flow.state?.petMetaobjectId;
      return metaobjectId ? `${prefix}/profile?pet=${encodeURIComponent(metaobjectId)}` : `${prefix}/profile`;
    }
    return `${prefix}/quiz/${step}`;
  }

  trackEvent(name, params = {}) {
    const traits = this.quizTraits();
    this.pushTraits(traits, false);
    this.gtag('event', name, Object.assign({ flow_name: this.flowName }, traits, params));
  }

  trackCompletion(context = {}) {
    this.trackEvent('flow_complete', context);
  }

  trackAddToCart(context = {}) {
    this.trackEvent('flow_add_to_cart', context);
    if (context.content_ids && context.value !== undefined) {
      this.trackFacebookAddToCart(context);
    }
  }

  trackResume(petName, source) {
    this.trackEvent('flow_resume_used', { pet_name: petName, source });
  }

  /** User chose "Continue without email" on Wizard 2 (distinct from Lead / submitted email). */
  trackQuizEmailSkipped() {
    this.trackEvent('quiz_email_skipped', { step: 'email' });
  }

  trackError(message, context = {}) {
    this.trackEvent('flow_error', Object.assign({ message }, context));
  }

  fbq() {
    if (typeof window.fbq === 'function') window.fbq.apply(window, arguments);
  }

  generateEventId(eventType) {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `wizard-2-${eventType}-${crypto.randomUUID()}`;
      }
      return `wizard-2-${eventType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    } catch (e) {
      return `wizard-2-${eventType}-${Date.now()}-${Math.random()}`;
    }
  }

  getStepNumber(step) {
    const idx = this.STEP_ORDER.indexOf(step);
    return idx >= 0 ? idx + 1 : 0;
  }

  trackFacebookStepView(step) {
    const stepNumber = this.getStepNumber(step);
    const eventId = this.generateEventId('step');
    const eventData = {
      step_name: step,
      step_number: stepNumber,
      flow_name: this.flowName,
      content_name: `Quiz Step ${stepNumber}: ${step}`
    };
    this.fbq('trackCustom', 'QuizStepViewed', eventData);
    this.trackFacebookConversionAPI('QuizStepViewed', eventId, eventData);
  }

  trackFacebookLead(context = {}) {
    try {
      const eventId = this.generateEventId('lead');
      const petData = this.flow?.state?.getPetData?.() || {};
      const eventData = {
        content_name: 'Quiz Completed',
        flow_name: this.flowName,
        pet_name: context.pet_name || petData.name || null
      };
      this.fbq('track', 'Lead', eventData);
      this.trackFacebookConversionAPI('Lead', eventId, eventData, { includeUserData: true });
    } catch (e) {}
  }

  trackFacebookAddToCart(context) {
    const eventId = this.generateEventId('addtocart');
    const activeCurrency =
      (typeof window !== 'undefined' && window.Shopify?.currency?.active) || null;
    const stateCurrency = this.flow?.state?.presentmentCurrencyCode || null;
    const resolvedCurrency = (
      context.currency ||
      stateCurrency ||
      activeCurrency ||
      'EUR'
    ).toString().toUpperCase();
    const eventData = {
      content_ids: context.content_ids || [],
      contents: context.contents || [],
      value: context.value || 0,
      currency: resolvedCurrency,
      flow_name: this.flowName
    };
    this.fbq('track', 'AddToCart', eventData);
    this.trackFacebookConversionAPI('AddToCart', eventId, eventData, { includeUserData: true });
  }

  async trackFacebookConversionAPI(eventName, eventId, customData = {}, options = {}) {
    if (!this.backendApiUrl) return;
    try {
      let userData = null;
      if (options.includeUserData) {
        const petData = this.flow?.state?.getPetData?.() || {};
        const customerId = this.flow?.state?.customerId || null;
        let emailHash = null;
        if (petData.ownerEmail && crypto.subtle) {
          const enc = new TextEncoder().encode(petData.ownerEmail.toLowerCase().trim());
          const buf = await crypto.subtle.digest('SHA-256', enc);
          emailHash = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
        }
        if (emailHash || customerId) {
          userData = {};
          if (emailHash) userData.em = emailHash;
          if (customerId) userData.external_id = customerId.includes('/') ? customerId.split('/').pop() : customerId;
        }
      }
      const payload = {
        event_name: eventName,
        event_id: eventId,
        custom_data: customData,
        event_time: Math.floor(Date.now() / 1000),
        action_source: 'website'
      };
      if (userData && Object.keys(userData).length > 0) payload.user_data = userData;
      fetch(`${this.backendApiUrl}/api/facebook/conversion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});
    } catch (e) {}
  }
}

window.Wizard2Analytics = Wizard2Analytics;
